import { useMemo, useState } from "react";
import { IlamyCalendar } from "@ilamy/calendar";
import type { CalendarEvent } from "@ilamy/calendar";
import type { Event } from "../../reducers/apiTypes";
import { EventTag } from "../common/EventTag";
import {
    categoryColor,
    categoryLabel,
    formatEventTimeRange,
} from "../../utils/eventDisplay";
import {
    eventsToIlamyCalendar,
    ilamyEventToAppEvent,
    type IlamyCalendarView,
} from "../../utils/calendarEvents";

type Props = {
    events: Event[];
    onEventClick: (event: Event) => void;
};

const VIEW_OPTIONS: { id: IlamyCalendarView; label: string }[] = [
    { id: "day", label: "Day" },
    { id: "week", label: "Week" },
    { id: "month", label: "Month" },
];

function CalendarEventChip({ event }: { event: Event }) {
    const color = categoryColor(event.event_type);

    return (
        <div className="flex h-full min-h-0 flex-col gap-0.5 overflow-hidden rounded-lg border border-white/60 px-1.5 py-1 text-left shadow-sm">
            <span className="truncate text-[0.65rem] font-bold leading-tight text-foreground">
                {event.name}
            </span>
            <span className="truncate text-[0.6rem] font-medium text-muted">
                {formatEventTimeRange(event)}
            </span>
            <div className="hidden sm:flex">
                <EventTag
                    label={categoryLabel(event.event_type)}
                    color={color}
                />
            </div>
        </div>
    );
}

export function EventsCalendar({ events, onEventClick }: Props) {
    const [view, setView] = useState<IlamyCalendarView>("week");
    const calendarEvents = useMemo(() => eventsToIlamyCalendar(events), [events]);

    const handleEventClick = (calendarEvent: CalendarEvent) => {
        const appEvent = ilamyEventToAppEvent(calendarEvent);
        if (appEvent) onEventClick(appEvent);
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 md:px-8 md:pb-6">
            <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
                <h2 className="text-base font-bold text-foreground md:text-lg">
                    {events.length} events
                </h2>
                <div
                    className="flex rounded-full bg-surface-secondary p-1"
                    role="group"
                    aria-label="Calendar view"
                >
                    {VIEW_OPTIONS.map(({ id, label }) => {
                        const selected = view === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                aria-pressed={selected}
                                onClick={() => setView(id)}
                                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors sm:px-4 ${
                                    selected
                                        ? "bg-white text-foreground shadow-sm"
                                        : "text-muted hover:text-foreground"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="calendar-shell min-h-0 flex-1 overflow-hidden rounded-3xl bg-white shadow-[var(--surface-shadow)]">
                <IlamyCalendar
                    key={view}
                    events={calendarEvents}
                    initialView={view}
                    firstDayOfWeek="sunday"
                    timeFormat="12-hour"
                    disableCellClick
                    disableDragAndDrop
                    onEventClick={handleEventClick}
                    renderEvent={(calendarEvent) => {
                        const appEvent = ilamyEventToAppEvent(calendarEvent);
                        if (!appEvent) return null;
                        return <CalendarEventChip event={appEvent} />;
                    }}
                />
            </div>
        </div>
    );
}

export default EventsCalendar;
